import React, { useEffect, useState } from "react";
import {
  Row,
  Col,
  Card,
  Button,
  Modal,
  Form,
  Select,
  notification,
  Spin,
  Input,
  Space,
  DatePicker,
  Divider,
  message,
  Popover,
  Table,
} from "antd";
import {
  PlusOutlined,
  SearchOutlined,
  ShareAltOutlined,
  LinkedinOutlined,
  FacebookOutlined,
  TwitterOutlined,
  InstagramOutlined,
  WhatsAppOutlined,
} from "@ant-design/icons";
import { useSelector } from "react-redux";
import { SUPER_ADMIN, ADMIN } from "../../../Constants/Roles/roles";
import {
  issueBadge,
  getBadgesByStudent,
  getAllBadges,
  getAllStudentBadges,
  getAllUsers,
  createBadge,
  getAllCoursesByRole,
} from "../../../Constants/Api/functions/function";
import dayjs from "dayjs";
import "./style.scss";

const Badges = () => {
  const [api, contextHolder] = notification.useNotification();
  const [isLoading, setIsLoading] = useState(false);
  const [badges, setBadges] = useState([]);
  const [studentBadges, setStudentBadges] = useState([]);
  const [allBadges, setAllBadges] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [allCourses, setAllCourses] = useState([]);
  const [issueBadgeModalOpen, setIssueBadgeModalOpen] = useState(false);
  const [createBadgeModalOpen, setCreateBadgeModalOpen] = useState(false);
  const [form] = Form.useForm();
  const [createBadgeForm] = Form.useForm();
  const userProfile = useSelector((state) => state.userProfile.userProfile);
  const [searchText, setSearchText] = useState("");
  const [filteredBadges, setFilteredBadges] = useState([]);
  const isAdminOrSuperAdmin =
    userProfile?.role === SUPER_ADMIN || userProfile?.role === ADMIN;

  const columns = [
    {
      title: "Badge Image",
      dataIndex: "BadgeImageURL",
      key: "BadgeImageURL",
      render: (imageUrl) =>
        imageUrl ? (
          <img
            src={imageUrl}
            alt="Badge"
            style={{ width: "50px", height: "50px", objectFit: "cover" }}
          />
        ) : (
          "No Image"
        ),
    },
    {
      title: "Badge Name",
      dataIndex: "BadgeName",
      key: "BadgeName",
    },
    {
      title: "Description",
      dataIndex: "BadgeDescription",
      key: "BadgeDescription",
    },
    {
      title: "Course",
      dataIndex: "courseName",
      key: "courseName",
      render: (courseName) => courseName || "N/A",
    },
    {
      title: "Student",
      dataIndex: "studentName",
      key: "studentName",
      render: (studentName) => studentName || "N/A",
    },
    {
      title: "Awarded Date",
      dataIndex: "AwardDate",
      key: "AwardDate",
      render: (awardDate) => awardDate || "N/A",
    },
  ];

  useEffect(() => {
    const handleGetAllStudentBadges = async () => {
      setIsLoading(true);
      try {
        const data = await getAllStudentBadges();
        setBadges(data);
        setFilteredBadges(data);
      } catch (error) {
        console.error("handleGetAllStudentBadges", error);
        api.error({
          message: "Error",
          description: "Failed to fetch badges",
        });
      }
      setIsLoading(false);
    };

    const handleGetStudentBadges = async () => {
      setIsLoading(true);
      try {
        const data = await getBadgesByStudent(userProfile?.id);
        setStudentBadges(data);
        setFilteredBadges(data);
      } catch (error) {
        console.error("handleGetStudentBadges", error);
        api.error({
          message: "Error",
          description: "Failed to fetch your badges",
        });
      }
      setIsLoading(false);
    };

    const fetchBadges = async () => {
      if (userProfile?.role === SUPER_ADMIN) {
        await handleGetAllStudentBadges();
      } else {
        await handleGetStudentBadges();
      }
    };

    if (userProfile?.id) {
      fetchBadges();
    }
  }, [userProfile?.id, userProfile?.role, api]);

  const handleOpenIssueBadgeModal = async () => {
    setIsLoading(true);
    try {
      const [badgesData, usersData] = await Promise.all([
        getAllBadges(),
        getAllUsers(),
      ]);
      setAllBadges(badgesData);
      setAllUsers(usersData);
      setIssueBadgeModalOpen(true);
    } catch (error) {
      console.error("Error fetching data:", error);
      message.error("Failed to load badges and students");
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenCreateBadgeModal = async () => {
    setIsLoading(true);
    try {
      const coursesData = await getAllCoursesByRole(
        userProfile?.role,
        userProfile?.id
      );
      setAllCourses(coursesData);
      setCreateBadgeModalOpen(true);
    } catch (error) {
      console.error("Error fetching courses:", error);
      message.error("Failed to load courses");
    } finally {
      setIsLoading(false);
    }
  };

  const handleIssueBadge = async (values) => {
    setIsLoading(true);
    try {
      const badgeData = {
        StudentID: values.studentId,
        BadgeID: values.badgeId,
        AwardDate: values.awardDate
          ? values.awardDate.format("YYYY-MM-DD")
          : new Date().toISOString().split("T")[0],
      };

      const res = await issueBadge(badgeData);
      if (res && res.status === 200) {
        api.success({
          message: "Success",
          description: "Badge issued successfully",
        });
        form.resetFields();
        setIssueBadgeModalOpen(false);
        // Refresh the badges list
        const data = await getAllStudentBadges();
        setBadges(data);
        setFilteredBadges(data);
      } else {
        api.error({
          message: "Error",
          description: res?.data?.message || "Failed to issue badge",
        });
      }
    } catch (error) {
      console.error("handleIssueBadge", error);
      api.error({
        message: "Error",
        description: "Something went wrong",
      });
    }
    setIsLoading(false);
  };

  const handleCreateBadge = async (values) => {
    setIsLoading(true);
    try {
      const badgeData = {
        BadgeName: values.badgeName,
        BadgeDescription: values.badgeDescription,
        BadgeImageURL: values.badgeImageURL,
        CourseID: values.courseId,
      };

      const res = await createBadge(badgeData);
      if (res && res.status === 200) {
        api.success({
          message: "Success",
          description: "Badge created successfully",
        });
        createBadgeForm.resetFields();
        setCreateBadgeModalOpen(false);
        // Refresh the badges list
        const data = await getAllStudentBadges();
        setBadges(data);
        setFilteredBadges(data);
      } else {
        api.error({
          message: "Error",
          description: res?.data?.message || "Failed to create badge",
        });
      }
    } catch (error) {
      console.error("handleCreateBadge", error);
      api.error({
        message: "Error",
        description: "Something went wrong",
      });
    }
    setIsLoading(false);
  };

  const handleSearch = (value) => {
    setSearchText(value);

    if (!value.trim()) {
      setFilteredBadges(
        userProfile?.role === SUPER_ADMIN ? badges : studentBadges
      );
      return;
    }

    const dataToSearch =
      userProfile?.role === SUPER_ADMIN ? badges : studentBadges;
    const filtered = dataToSearch.filter(
      (badge) =>
        badge?.BadgeName?.toLowerCase().includes(value.toLowerCase()) ||
        badge?.BadgeDescription?.toLowerCase().includes(value.toLowerCase()) ||
        badge?.CourseName?.toLowerCase().includes(value.toLowerCase()) ||
        badge?.StudentName?.toLowerCase().includes(value.toLowerCase())
    );

    setFilteredBadges(filtered);
  };

  const handleShareBadge = (badge, platform) => {
    const badgeText = `I just earned the ${badge.BadgeName} badge in ${
      badge.CourseName || "Digital Academy"
    }!`;
    const shareUrl = window.location.href;
    let url = "";

    switch (platform) {
      case "linkedin":
        url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
          shareUrl
        )}`;
        break;
      case "facebook":
        url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
          shareUrl
        )}`;
        break;
      case "twitter":
        url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
          badgeText
        )}&url=${encodeURIComponent(shareUrl)}`;
        break;
      case "whatsapp":
        url = `https://wa.me/?text=${encodeURIComponent(
          badgeText + " " + shareUrl
        )}`;
        break;
      case "instagram":
        message.info(
          "Instagram doesn't support direct sharing. Copy the link and share manually."
        );
        navigator.clipboard.writeText(`${badgeText}\n${shareUrl}`);
        return;
      default:
        break;
    }

    if (url) {
      window.open(url, "_blank", "width=600,height=400");
      message.success(`Shared on ${platform}!`);
    }
  };

  const renderShareMenu = (badge) => (
    <Space direction="vertical" style={{ width: "100%" }}>
      <Button
        type="text"
        block
        size="small"
        icon={<LinkedinOutlined />}
        onClick={() => handleShareBadge(badge, "linkedin")}
      >
        LinkedIn
      </Button>
      <Button
        type="text"
        block
        size="small"
        icon={<FacebookOutlined />}
        onClick={() => handleShareBadge(badge, "facebook")}
      >
        Facebook
      </Button>
      <Button
        type="text"
        block
        size="small"
        icon={<TwitterOutlined />}
        onClick={() => handleShareBadge(badge, "twitter")}
      >
        Twitter
      </Button>
      <Button
        type="text"
        block
        size="small"
        icon={<WhatsAppOutlined />}
        onClick={() => handleShareBadge(badge, "whatsapp")}
      >
        WhatsApp
      </Button>
      <Button
        type="text"
        block
        size="small"
        icon={<InstagramOutlined />}
        onClick={() => handleShareBadge(badge, "instagram")}
      >
        Instagram
      </Button>
    </Space>
  );

  const renderBadgeCard = (badge) => (
    <Col
      xs={24}
      sm={12}
      md={8}
      lg={6}
      key={badge.BadgeID || badge.StudentBadgeID}
    >
      <Card
        hoverable
        cover={
          badge.BadgeImageURL ? (
            <img
              alt={badge.BadgeName}
              src={badge.BadgeImageURL}
              style={{ height: "150px", objectFit: "cover" }}
            />
          ) : (
            <div
              style={{
                height: "150px",
                background: "#f0f0f0",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <span style={{ color: "#999" }}>No Image</span>
            </div>
          )
        }
        className="badge-card"
      >
        <Card.Meta
          title={badge.BadgeName}
          description={badge.BadgeDescription}
        />
        <div style={{ marginTop: "10px", fontSize: "12px", color: "#666" }}>
          <p>
            <strong>Course:</strong> {badge.courseName || "N/A"}
          </p>
          {userProfile?.role === SUPER_ADMIN && (
            <p>
              <strong>Student:</strong> {badge.studentName}
            </p>
          )}
          <p>
            <strong>Awarded:</strong> {badge.AwardDate}
          </p>
        </div>
        <div style={{ marginTop: "15px" }}>
          <Popover
            content={renderShareMenu(badge)}
            title="Share Badge"
            trigger="click"
            placement="topRight"
          >
            <Button
              type="primary"
              size="small"
              icon={<ShareAltOutlined />}
              block
            >
              Share
            </Button>
          </Popover>
        </div>
      </Card>
    </Col>
  );

  return (
    <div className="badges-container">
      {contextHolder}

      <div className="badges-header">
        <div>
          <h1>
            {userProfile?.role === SUPER_ADMIN ? "All Badges" : "My Badges"}
          </h1>
        </div>
        <div>
          {userProfile?.role === SUPER_ADMIN && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleOpenCreateBadgeModal}
            >
              Create Badge
            </Button>
          )}
        </div>
      </div>

      <div className="badges-actions">
        <Space>
          <Input
            placeholder="Search badges by name, description, course..."
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => handleSearch(e.target.value)}
            style={{ width: "300px" }}
            allowClear
          />
          {userProfile?.role === SUPER_ADMIN && (
            <Space>
              {/* <Button
                                type='primary'
                                icon={<PlusOutlined />}
                                onClick={handleOpenCreateBadgeModal}
                            >
                                Create Badge
                            </Button> */}
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleOpenIssueBadgeModal}
              >
                Issue Badge
              </Button>
            </Space>
          )}
        </Space>
      </div>

      <Divider />

      <Spin spinning={isLoading} tip="Loading badges...">
        {filteredBadges && filteredBadges.length > 0 ? (
          isAdminOrSuperAdmin ? (
            <Table
              dataSource={filteredBadges}
              columns={columns}
              rowKey={(badge) => badge.BadgeID || badge.StudentBadgeID}
              pagination={false}
            />
          ) : (
            <Row gutter={[16, 16]}>
              {filteredBadges.map((badge) => renderBadgeCard(badge))}
            </Row>
          )
        ) : (
          <Card>
            <p style={{ textAlign: "center", color: "#999" }}>
              {userProfile?.role === SUPER_ADMIN
                ? "No badges awarded yet"
                : "You haven't earned any badges yet"}
            </p>
          </Card>
        )}
      </Spin>

      {userProfile?.role === SUPER_ADMIN && (
        <>
          <Modal
            title="Create New Badge"
            open={createBadgeModalOpen}
            onCancel={() => {
              setCreateBadgeModalOpen(false);
              createBadgeForm.resetFields();
            }}
            footer={null}
            width={600}
          >
            <Form
              form={createBadgeForm}
              layout="vertical"
              onFinish={handleCreateBadge}
            >
              <Form.Item
                label="Badge Name"
                name="badgeName"
                rules={[
                  { required: true, message: "Please enter badge name!" },
                ]}
              >
                <Input placeholder="Enter badge name" />
              </Form.Item>

              <Form.Item label="Badge Description" name="badgeDescription">
                <Input.TextArea
                  placeholder="Enter badge description (optional)"
                  rows={3}
                />
              </Form.Item>

              <Form.Item label="Badge Image URL" name="badgeImageURL">
                <Input placeholder="Enter badge image URL (optional)" />
              </Form.Item>

              <Form.Item
                label="Course"
                name="courseId"
                rules={[{ required: true, message: "Please select a course!" }]}
              >
                <Select
                  showSearch
                  placeholder="Select course"
                  optionFilterProp="label"
                  filterOption={(input, option) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {allCourses.map((course) => (
                    <Select.Option
                      key={course.id}
                      value={course.id}
                      label={course.name}
                    >
                      {course.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={isLoading}
                  block
                >
                  Create Badge
                </Button>
              </Form.Item>
            </Form>
          </Modal>

          <Modal
            title="Issue Badge to Student"
            open={issueBadgeModalOpen}
            onCancel={() => {
              setIssueBadgeModalOpen(false);
              form.resetFields();
            }}
            footer={null}
            width={600}
          >
            <Form form={form} layout="vertical" onFinish={handleIssueBadge}>
              <Form.Item
                label="Student"
                name="studentId"
                rules={[
                  { required: true, message: "Please select a student!" },
                ]}
              >
                <Select
                  showSearch
                  placeholder="Select student"
                  optionFilterProp="label"
                  filterOption={(input, option) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {allUsers.map((user) => (
                    <Select.Option
                      key={user.id}
                      value={user.id}
                      label={`${user.indexNumber} - ${user.name} (${user.email})`}
                    >
                      {user.indexNumber} - {user.name} ({user.email})
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                label="Badge"
                name="badgeId"
                rules={[{ required: true, message: "Please select a badge!" }]}
              >
                <Select
                  showSearch
                  placeholder="Select badge"
                  optionFilterProp="label"
                  filterOption={(input, option) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {allBadges.map((badge) => (
                    <Select.Option
                      key={badge.BadgeID}
                      value={badge.BadgeID}
                      label={`${badge.BadgeName} - ${badge.courseName}`}
                    >
                      {badge.BadgeName} - {badge.courseName}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                label="Award Date"
                name="awardDate"
                initialValue={dayjs()}
              >
                <DatePicker style={{ width: "100%" }} />
              </Form.Item>

              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={isLoading}
                  block
                >
                  Issue Badge
                </Button>
              </Form.Item>
            </Form>
          </Modal>
        </>
      )}
    </div>
  );
};

export default Badges;
